import { useState, useRef, useEffect } from "react";
import { MessageCircle, X, Send, Loader2, Bot, User, Sparkles, Mail, Mic, Volume2, Square, AudioLines } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import api, { formatApiError } from "@/lib/api";
import { streamChat } from "@/lib/streamChat";
import { transcribeAudio, synthesizeSpeech } from "@/lib/voice";
import VoiceMode from "@/components/VoiceMode";
import { toast } from "sonner";

const SUGGESTIONS = [
  "What does Vvvoraa do?",
  "Are you hiring?",
  "How can I contact your team?",
];

function makeSession() {
  return Math.random().toString(36).slice(2) + Date.now().toString(36);
}

export default function ChatWidget() {
  const [open, setOpen] = useState(false);
  const [sessionId] = useState(makeSession);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [userMsgCount, setUserMsgCount] = useState(0);

  const [showLead, setShowLead] = useState(false);
  const [leadDone, setLeadDone] = useState(false);
  const [leadName, setLeadName] = useState("");
  const [leadEmail, setLeadEmail] = useState("");
  const [leadSending, setLeadSending] = useState(false);

  const [recording, setRecording] = useState(false);
  const [speakingIdx, setSpeakingIdx] = useState(null);
  const [speakingLoading, setSpeakingLoading] = useState(null);
  const mediaRef = useRef(null);
  const chunksRef = useRef([]);
  const audioRef = useRef(null);
  const voiceTurnRef = useRef(false);
  const [voiceOpen, setVoiceOpen] = useState(false);

  const onVoiceTurn = (u, a) => {
    setMessages((m) => [...m, { role: "user", content: u }, { role: "ai", content: a }]);
    const nextCount = userMsgCount + 1;
    setUserMsgCount(nextCount);
    if (nextCount >= 2 && !leadDone) setShowLead(true);
  };

  const endRef = useRef(null);

  useEffect(() => {
    if (open) endRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages, loading, open, showLead]);

  useEffect(() => {
    const handler = () => setOpen(true);
    window.addEventListener("vvvoraa:open-chat", handler);
    return () => window.removeEventListener("vvvoraa:open-chat", handler);
  }, []);

  const speak = async (text, idx) => {
    if (!text || !text.trim()) return;
    if (speakingIdx === idx) {
      audioRef.current?.pause();
      setSpeakingIdx(null);
      return;
    }
    setSpeakingLoading(idx);
    try {
      const url = await synthesizeSpeech(text);
      audioRef.current?.pause();
      const audio = new Audio(url);
      audioRef.current = audio;
      setSpeakingIdx(idx);
      audio.onended = () => setSpeakingIdx(null);
      await audio.play();
    } catch (e) {
      toast.error(e.message || "Voice playback failed.");
    } finally {
      setSpeakingLoading(null);
    }
  };

  const toggleRecording = async () => {
    if (recording) {
      mediaRef.current?.stop();
      setRecording(false);
      return;
    }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const rec = new MediaRecorder(stream);
      mediaRef.current = rec;
      chunksRef.current = [];
      rec.ondataavailable = (e) => { if (e.data.size) chunksRef.current.push(e.data); };
      rec.onstop = async () => {
        stream.getTracks().forEach((t) => t.stop());
        const blob = new Blob(chunksRef.current, { type: rec.mimeType || "audio/webm" });
        try {
          const text = await transcribeAudio(blob);
          if (text && text.trim()) {
            voiceTurnRef.current = true;
            ask(text);
          } else {
            toast.error("Couldn't hear anything — try again.");
          }
        } catch (e) {
          toast.error(e.message || "Voice transcription failed.");
        }
      };
      rec.start();
      setRecording(true);
    } catch {
      toast.error("Microphone access denied.");
    }
  };

  const ask = async (text) => {
    const q = (text ?? input).trim();
    if (!q || loading) return;
    setInput("");
    const aiIndex = messages.length + 1;
    setMessages((m) => [...m, { role: "user", content: q }]);
    const nextCount = userMsgCount + 1;
    setUserMsgCount(nextCount);
    setLoading(true);
    // Add an empty AI bubble that fills up token-by-token
    setMessages((m) => [...m, { role: "ai", content: "" }]);
    try {
      const reply = await streamChat({
        sessionId,
        message: q,
        onChunk: (full) => {
          setMessages((m) => {
            const copy = [...m];
            copy[copy.length - 1] = { role: "ai", content: full };
            return copy;
          });
        },
      });
      if (voiceTurnRef.current && reply) speak(reply, aiIndex);
      voiceTurnRef.current = false;
    } catch (err) {
      const msg = err.message || "The assistant is unavailable right now.";
      setMessages((m) => {
        const copy = [...m];
        copy[copy.length - 1] = { role: "ai", content: msg };
        return copy;
      });
    } finally {
      setLoading(false);
      // Auto-prompt for a lead once genuine interest is shown (2nd message)
      if (nextCount >= 2 && !leadDone) setShowLead(true);
    }
  };

  const submitLead = async () => {
    const email = leadEmail.trim();
    if (!email || !/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(email)) { toast.error("Please enter a valid email"); return; }
    setLeadSending(true);
    try {
      const context = messages.filter((m) => m.role === "user").map((m) => m.content).slice(-3).join(" | ");
      await api.post("/leads", { email, name: leadName.trim(), message: context, source: "chat-widget" });
      setLeadDone(true);
      setShowLead(false);
      setMessages((m) => [...m, { role: "ai", content: `Thanks${leadName.trim() ? ", " + leadName.trim() : ""}! Our team will reach out at ${email} shortly. Meanwhile, ask me anything else.` }]);
      toast.success("We'll be in touch soon");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Could not submit. Please try again.");
    } finally {
      setLeadSending(false);
    }
  };

  const LeadForm = (
    <div className="rounded-2xl border border-[#4F8CFF]/25 bg-[#4F8CFF]/[0.06] p-4" data-testid="chat-lead-form">
      <div className="flex items-center gap-2 text-sm text-white">
        <Mail className="h-4 w-4 text-[#4F8CFF]" />
        <span className="font-medium">Want us to build your AI agent?</span>
      </div>
      <p className="mt-1 text-xs text-slate-400">Leave your email and our team will reach out.</p>
      <div className="mt-3 space-y-2">
        <Input value={leadName} onChange={(e) => setLeadName(e.target.value)} placeholder="Your name (optional)" className="h-9 bg-white/[0.04] border-white/12 text-white placeholder:text-slate-500 text-sm" data-testid="chat-lead-name" />
        <Input value={leadEmail} onChange={(e) => setLeadEmail(e.target.value)} placeholder="you@company.com" type="email" className="h-9 bg-white/[0.04] border-white/12 text-white placeholder:text-slate-500 text-sm" data-testid="chat-lead-email" />
      </div>
      <div className="mt-3 flex items-center gap-2">
        <Button onClick={submitLead} disabled={leadSending} className="flex-1 h-9 bg-[#4F8CFF] hover:bg-[#3f7af0] text-white rounded-full text-sm" data-testid="chat-lead-submit">
          {leadSending ? <Loader2 className="h-4 w-4 animate-spin" /> : "Request a callback"}
        </Button>
        <Button onClick={() => setShowLead(false)} variant="ghost" className="h-9 text-slate-400 hover:text-white hover:bg-white/5 rounded-full text-sm" data-testid="chat-lead-dismiss">
          Not now
        </Button>
      </div>
    </div>
  );

  return (
    <>
      <button
        onClick={() => setOpen((o) => !o)}
        className="fixed bottom-5 right-5 z-50 flex h-14 w-14 items-center justify-center rounded-full bg-gradient-to-br from-[#4F8CFF] to-[#7C5CFF] text-white shadow-lg shadow-[#4F8CFF]/30 transition-transform hover:scale-105 active:scale-95"
        aria-label={open ? "Close chat" : "Open chat"}
        data-testid="chat-widget-toggle"
      >
        {open ? <X className="h-6 w-6" /> : <MessageCircle className="h-6 w-6" />}
      </button>

      {open && (
        <div
          className="fixed bottom-24 right-5 z-50 w-[calc(100vw-2.5rem)] max-w-sm vora-glass rounded-3xl flex flex-col h-[560px] max-h-[78vh] overflow-hidden animate-in fade-in slide-in-from-bottom-4 duration-200"
          data-testid="chat-widget-panel"
        >
          <div className="flex items-center justify-between px-5 py-3.5 border-b border-white/8">
            <span className="inline-flex items-center gap-2 text-sm text-white">
              <span className="flex h-7 w-7 items-center justify-center rounded-lg bg-gradient-to-br from-[#4F8CFF] to-[#7C5CFF]">
                <Sparkles className="h-4 w-4 text-white" />
              </span>
              Vvvoraa AI
            </span>
            <div className="flex items-center gap-1.5">
              <button
                onClick={() => setVoiceOpen(true)}
                className="inline-flex items-center gap-1.5 rounded-full border border-[#7C5CFF]/30 bg-[#7C5CFF]/10 px-2.5 py-1 text-[11px] text-[#b3a1ff] hover:bg-[#7C5CFF]/20 transition-colors"
                data-testid="chat-voice-mode-btn"
                aria-label="Start voice mode"
              >
                <AudioLines className="h-3 w-3" /> Voice
              </button>
              {!leadDone && (
                <button
                  onClick={() => setShowLead(true)}
                  className="inline-flex items-center gap-1.5 rounded-full border border-[#4F8CFF]/30 bg-[#4F8CFF]/10 px-2.5 py-1 text-[11px] text-[#8fb4ff] hover:bg-[#4F8CFF]/20 transition-colors"
                  data-testid="chat-get-in-touch"
                >
                  <Mail className="h-3 w-3" /> Get in touch
                </button>
              )}
              <button onClick={() => setOpen(false)} className="text-slate-400 hover:text-white transition-colors" data-testid="chat-widget-close">
                <X className="h-4 w-4" />
              </button>
            </div>
          </div>

          <div className="flex-1 overflow-y-auto p-4 space-y-4">
            {messages.length === 0 && (
              <div className="h-full flex flex-col items-center justify-center text-center">
                <Bot className="h-9 w-9 text-[#4F8CFF]" />
                <p className="mt-3 text-sm text-slate-400 max-w-xs">Hi! Ask me anything about Vvvoraa.</p>
                <div className="mt-4 flex flex-wrap justify-center gap-2">
                  {SUGGESTIONS.map((s) => (
                    <button
                      key={s}
                      onClick={() => ask(s)}
                      className="rounded-full border border-white/12 bg-white/[0.03] px-3 py-1.5 text-xs text-slate-300 hover:text-white hover:border-[#4F8CFF]/40 transition-colors"
                      data-testid="chat-widget-suggestion"
                    >
                      {s}
                    </button>
                  ))}
                </div>
              </div>
            )}
            {messages.map((m, i) => {
              // While streaming, the last AI bubble starts empty — don't render it until the first token
              if (m.role === "ai" && m.content === "" && i === messages.length - 1) return null;
              return (
                <div key={i} className={`flex gap-2.5 ${m.role === "user" ? "flex-row-reverse" : ""}`} data-testid={`chat-widget-msg-${m.role}`}>
                  <span className={`flex h-7 w-7 shrink-0 items-center justify-center rounded-lg ${m.role === "user" ? "bg-white/10 text-white" : "bg-[#4F8CFF]/15 text-[#4F8CFF]"}`}>
                    {m.role === "user" ? <User className="h-3.5 w-3.5" /> : <Bot className="h-3.5 w-3.5" />}
                  </span>
                  <div className={`max-w-[80%] rounded-2xl px-3.5 py-2 text-sm leading-relaxed whitespace-pre-wrap ${m.role === "user" ? "bg-[#4F8CFF] text-white" : "bg-white/[0.04] text-slate-200 border border-white/8"}`}>
                    {m.content}
                    {m.role === "ai" && m.content && (
                      <button
                        onClick={() => speak(m.content, i)}
                        className="mt-1.5 inline-flex items-center gap-1 text-[11px] text-slate-500 hover:text-[#4F8CFF] transition-colors"
                        data-testid={`chat-speak-${i}`}
                        aria-label="Read aloud"
                      >
                        {speakingLoading === i ? <Loader2 className="h-3 w-3 animate-spin" /> : speakingIdx === i ? <Square className="h-3 w-3" /> : <Volume2 className="h-3 w-3" />}
                        {speakingIdx === i ? "Stop" : "Listen"}
                      </button>
                    )}
                  </div>
                </div>
              );
            })}
            {loading && (messages.length === 0 || messages[messages.length - 1]?.content === "") && (
              <div className="flex gap-2.5" data-testid="chat-widget-typing">
                <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-lg bg-[#4F8CFF]/15 text-[#4F8CFF]"><Bot className="h-3.5 w-3.5" /></span>
                <div className="rounded-2xl px-4 py-3 bg-white/[0.04] border border-white/8">
                  <span className="flex gap-1">
                    <span className="h-2 w-2 rounded-full bg-slate-400 animate-bounce" style={{ animationDelay: "0ms" }} />
                    <span className="h-2 w-2 rounded-full bg-slate-400 animate-bounce" style={{ animationDelay: "150ms" }} />
                    <span className="h-2 w-2 rounded-full bg-slate-400 animate-bounce" style={{ animationDelay: "300ms" }} />
                  </span>
                </div>
              </div>
            )}
            {showLead && !leadDone && LeadForm}
            <div ref={endRef} />
          </div>

          <form onSubmit={(e) => { e.preventDefault(); ask(); }} className="flex items-center gap-2 p-3 border-t border-white/8">
            <Input
              value={input}
              onChange={(e) => setInput(e.target.value)}
              placeholder={recording ? "Listening… tap to stop" : "Ask Vvvoraa AI…"}
              className="bg-white/[0.03] border-white/12 text-white placeholder:text-slate-500 focus-visible:ring-[#4F8CFF]"
              data-testid="chat-widget-input"
            />
            <Button
              type="button"
              onClick={toggleRecording}
              disabled={loading}
              className={`rounded-full h-10 w-10 p-0 shrink-0 ${recording ? "bg-red-500 hover:bg-red-600 text-white animate-pulse" : "bg-white/[0.06] hover:bg-white/10 text-slate-300"}`}
              data-testid="chat-widget-mic"
              aria-label={recording ? "Stop recording" : "Voice input"}
            >
              <Mic className="h-4 w-4" />
            </Button>
            <Button type="submit" disabled={loading || !input.trim()} className="bg-[#4F8CFF] hover:bg-[#3f7af0] text-white rounded-full h-10 w-10 p-0 shrink-0" data-testid="chat-widget-send">
              {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
            </Button>
          </form>
        </div>
      )}

      {voiceOpen && (
        <VoiceMode
          sessionId={sessionId}
          onTurn={onVoiceTurn}
          onClose={() => setVoiceOpen(false)}
        />
      )}
    </>
  );
}
